import React, { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import { getLocationFromAddress } from '../lib/utils/map';
import { getPermissions, Permissions } from '../lib/permissions';
import { useSpots } from '../lib/spots';
import { UserPage } from '../components/UserPage';
import { Section } from '../components/Section';
import { Button } from '../components/Button';
import { Flash } from '../components/Flash';


export const RegisterPage: FC = () => {
    const { register: ref, handleSubmit, errors, setError, clearErrors } = useForm({
        criteriaMode: "all",
    });
    const [loading, setLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [flash, setFlash] = useState<string | undefined>();
    const [selectedImage, setSelectedImage] = useState<{ body: string; mimeType: string } | null>(null);
    const { register } = useSpots();
    const history = useHistory()
    const [userPermissions, setUserPermissions] = useState<Permissions | null>(null);

    useEffect(() => {
        async function initialize() {
            const permissions = await getPermissions();
            if (!permissions.create) {
                // アプリからのアクセス時には決済関連の情報を見せてはいけないのでリダイレクト先を`/map`にする必要がある
                if (window.navigator.userAgent === 'Salon.jp App/iOS') {
                    history.push(
                        '/map',
                        { flash: '西野亮廣エンタメ研究所への入会が必要となります。アプリからはご利用いただけません。' },
                    );
                    return;
                };
                let redirectUrl = '/payments/pay/nishino';
                if (permissions.status === 'pause') {
                    redirectUrl = '/payments/suspend/1hc1srjr-johu-o7eg-33xn-r8u88p2slizf';
                } else if (permissions.status === 'unpaid') {
                    redirectUrl = '/mypages#payments';
                };
                window.location.href = redirectUrl;
                return;
            };
            try {
                const { data } = await axios.get('/api/profile_status');
                if (data.public) {
                    setUserPermissions(permissions);
                    setIsInitialized(true);
                    return;
                };
                window.location.href = '/mypages/edit';
            } catch {
                window.location.href = '/';
            };
        };
        initialize();
    }, []);

    async function onSubmit(data: any) {
        if (loading) return false;
        setLoading(true);

        const fullAddress = data.pref + data.city + data.address;
        let location;
        try {
            location = await getLocationFromAddress(fullAddress);
            clearErrors('pref');
            clearErrors('city');
            clearErrors('address');
        } catch {
            setError('pref', {
                message: '入力された住所から座標を特定できませんでした。',
                shouldFocus: true,
            });
            setError('city', {
                message: '入力内容をご確認ください。',
            });
            setError('address', {
                message: '入力内容をご確認ください。',
            })
            setLoading(false);
            return false;
        };
        let mimeType;
        let body;
        if (data.cover_image.length > 0) {
            const reader = new FileReader();
            mimeType = data.cover_image[0].type;
            reader.readAsDataURL(data.cover_image[0]);
            body = await new Promise<string>((resolve) => {
                reader.onload = (loaded) => {
                    const body = loaded.target?.result ?? null;
                    // @ts-ignore
                    resolve(body);
                };
            });
        };
        let params;
        if (!body) {
            params = {
                ...data,
                ...location,
            };
        } else {
            params = {
                ...data,
                ...location,
                cover_image: {
                    mimeType,
                    body: body.split(',')[1],
                },
            };
        }

        const { id, error, errors } = await register(params);

        if (errors) {
            for (const [name, msgs] of Object.entries<{ string: string }>(errors)) {
                for (const [_, msg] of Object.entries<string>(msgs)) {
                    setError(name, { type: 'manual', message: msg });
                }
            }
            setLoading(false);
            return false;
        }

        if (!error && id) {
            setLoading(false);
            history.push(
                `/map/spot/${id}`,
                { flash: '店舗情報を登録しました。' },
            );
        }

        setFlash('登録に失敗しました。');
        setLoading(false);
        return false;
    };

    function Cancel() {
        history.goBack();
    };

    return (
        <UserPage title="Salon.jp | お店を登録する">
            {flash && (
                <Flash
                    color="#a94442"
                    backgroundColor="#f2dede"
                    flash={flash}
                />
            )}
            {(userPermissions && userPermissions.create && isInitialized) && (
                <Section>
                    <div className="title">
                        <h1>お店の新規登録</h1>
                    </div>
                    <p className="message">あなたが働いているお店（プペル自販機含む）をマップに登録することができます。</p>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="wrapper">
                            <div className="label">ジャンル<span>（必須）</span></div>
                            <select ref={ref({ required: true })} name="genre" defaultValue="飲食店">
                                <option value="飲食店">飲食店</option>
                                <option value="ヘアサロン">ヘアサロン</option>
                                <option value="スナックキャンディ">スナックキャンディ</option>
                                <option value="宿泊">宿泊</option>
                                <option value="自販機">自販機</option>
                                <option value="保育園・幼稚園">保育園・幼稚園</option>
                                <option value="その他">その他</option>
                                <option value="居酒屋・バー">飲食店＞居酒屋・バー</option>
                                <option value="レストラン・カフェ">飲食店＞レストラン・カフェ</option>
                                <option value="スイーツ">飲食店＞スイーツ</option>
                                <option value="健康">その他＞健康</option>
                                <option value="ビューティ">その他＞ビューティ</option>
                                <option value="医療・福祉">その他＞医療・福祉</option>
                                <option value="小売">その他＞小売</option>
                                <option value="ビジネス">その他＞ビジネス</option>
                                <option value="レンタルスペース">その他＞レンタルスペース</option>
                                <option value="エンタメ・レジャー">その他＞エンタメ・レジャー</option>
                                <option value="習いごと">その他＞習いごと</option>
                                <option value="農業">その他＞農業</option>
                            </select>
                        </div>
                        <div className="wrapper">
                            <div className="label">お店の名前<span>（必須）</span></div>
                            <input type="text" ref={ref({ required: true })} name="name" />
                            {errors.name && <span className="error-message">入力内容をご確認ください。</span>}
                        </div>
                        <div className="wrapper">
                            <div className="label">住所（都道府県）<span>（必須）</span></div>
                            <input type="text" ref={ref({ required: true })} name="pref" />
                            {errors.pref && <span className="error-message">{errors.pref.message}</span>}
                        </div>
                        <div className="wrapper">
                            <div className="label">住所（市区町村）<span>（必須）</span></div>
                            <input type="text" ref={ref({ required: true })} name="city" />
                            {errors.city && <span className="error-message">{errors.city.message}</span>}
                        </div>
                        <div className="wrapper">
                            <div className="label">住所（それ以降）<span>（必須）</span></div>
                            <input type="text" ref={ref({ required: true })} name="address" />
                            {errors.address && <span className="error-message">{errors.address.message}</span>}
                        </div>
                        <div className="wrapper">
                            <div className="label">お店についての説明<span>（必須）</span></div>
                            <textarea cols={30} rows={8} ref={ref({ required: true })} name="description"></textarea>
                            {!errors.description && <div className="limit">255文字以内</div>}
                            <span className="error-message">
                                {errors.description?.type === 'required' && '入力内容をご確認ください。'}
                                {errors.description?.type === 'manual' && errors.description.message}
                            </span>
                        </div>
                        <div className="wrapper">
                            <div className="label">営業時間<span>（必須）</span></div>
                            <textarea cols={30} rows={5} ref={ref({ required: true })} name="opening_hours"></textarea>
                            {!errors.opening_hours && <div className="limit">255文字以内</div>}
                            <span className="error-message">
                                {errors.opening_hours?.type === 'required' && '入力内容をご確認ください。'}
                                {errors.opening_hours?.type === 'manual' && errors.opening_hours.message}
                            </span>
                        </div>
                        <div className="wrapper">
                            <div className="label">メンバー特典</div>
                            <p>※特典を用意する場合は、サロンメンバー用の連絡方法も明記してください。</p>
                            <textarea cols={30} rows={5} ref={ref} name="member_benefits"></textarea>
                            {!errors.member_benefits && <div className="limit">255文字以内</div>}
                            <span className="error-message">
                                {errors.member_benefits?.type === 'required' && '入力内容をご確認ください。'}
                                {errors.member_benefits?.type === 'manual' && errors.member_benefits.message}
                            </span>
                        </div>
                        <div className="wrapper">
                            <div className="label">カバー画像</div>
                            <p>※画像サイズは150×660推奨</p>
                            {!selectedImage && (
                                <label htmlFor="cover-image">
                                    <div className="cover-image-container">
                                        <div className="cover-image-selector">
                                            <div className="upload-button-container">
                                                <img className="upload-button" src={`${window.location.origin}/img/pin-plus.png`} alt="" />
                                                <span>画像をアップロード</span>
                                            </div>
                                        </div>
                                    </div>
                                </label>
                            )}
                            {selectedImage && (
                                <div className="cover-image-container">
                                    <div className="selected-container">
                                        <img className="selected-image" src={selectedImage.body} />
                                        <div className="change-image-button">
                                            <label htmlFor="cover-image">
                                                <div>
                                                    変更する
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <input
                                id="cover-image"
                                className="cover-image"
                                name="cover_image"
                                type="file"
                                ref={ref}
                                accept="image/*"
                                onChange={async (e) => {
                                    if (e.target.files && e.target.files.length > 0) {
                                        let mimeType;
                                        let body;
                                        const reader = new FileReader();
                                        mimeType = e.target.files[0].type;
                                        reader.readAsDataURL(e.target.files[0]);
                                        body = await new Promise<string>((resolve) => {
                                            reader.onload = (loaded) => {
                                                const body = loaded.target?.result ?? null;
                                                // @ts-ignore
                                                resolve(body);
                                            };
                                        });
                                        setSelectedImage({
                                            mimeType,
                                            body,
                                        });
                                    } else {
                                        setSelectedImage(null);
                                    }
                                }}
                            />
                        </div>
                        <div className="spot-options">
                            <div className="label">オプション（飲食店カテゴリーのみ）</div>
                            <div className="option">
                                <input type="checkbox" id="c1" ref={ref} name="delivery" />
                                <label htmlFor="c1" className="checkbox">デリバリーが可能</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c2" ref={ref} name="takeout" />
                                <label htmlFor="c2" className="checkbox">テイクアウトが可能</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c3" ref={ref} name="lunch" />
                                <label htmlFor="c3" className="checkbox">ランチ営業あり</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c4" ref={ref} name="dinner" />
                                <label htmlFor="c4" className="checkbox">ディナー営業あり</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c5" ref={ref} name="alcohol" />
                                <label htmlFor="c5" className="checkbox">アルコールを飲める</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c6" ref={ref} name="cafe" />
                                <label htmlFor="c6" className="checkbox">カフェ利用が可能</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c8" ref={ref} name="chimney_coffee" />
                                <label htmlFor="c8" className="checkbox">CHIMNEY COFFEE取扱</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c7" ref={ref} name="power_supply" />
                                <label htmlFor="c7" className="checkbox">電源利用が可能</label>
                            </div>
                        </div>
                        <div className="warning">
                            <p>※法令または公序良俗に反するお店の登録はご遠慮ください。</p>
                        </div>
                        <div className="action">
                            <Button className="cancel-button" backgroundColor="gray" type="button" onClick={Cancel}>
                                キャンセル
                            </Button>
                            <Button className="submit-button" type="submit">
                                登録する
                            </Button>
                        </div>
                    </form>
                </Section>
            )}

            <style jsx>{`
                .title {
                    padding-top: 50px;
                    padding-bottom: 26px;
                }
                .message {
                    font-size: 14px;
                    margin-bottom: 38px
                }
                form {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }
                .wrapper {
                    margin-bottom: 20px;
                }
                h1 {
                    font-weight: normal;
                    font-size: 20px;
                    margin: 0;
                }
                .checkbox {
                    cursor: pointer;
                }
                .label {
                    border-left: solid 2px black;
                    padding-left: 8px;
                    height: 22px;
                    margin-bottom: 4px;
                    font-size: 14px;
                }
                .label > span {
                    color: red;
                }
                input[type=checkbox] {
                    cursor: pointer;
                }
                input,
                textarea {
                    border: solid 1px rgba(0,0,0,0.08);
                    resize: none;
                    margin: 0;
                    padding: 0;
                }
                textarea {
                    width: 100%;
                    padding-top: 4px;
                    padding-left: 14px;
                    padding-right: 14px;
                }
                .cover-image {
                    display: none;
                }
                .cover-image-container {
                    position: relative;
                    width: 100%;
                    padding-top: 40%;
                }
                .cover-image-selector {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    background-image: linear-gradient(to right, #000, #000 3px, transparent 3px, transparent 8px),
                        linear-gradient(to right, #000, #000 3px, transparent 3px, transparent 8px),
                        linear-gradient(to bottom, #000, #000 3px, transparent 3px, transparent 8px),
                        linear-gradient(to bottom, #000, #000 3px, transparent 3px, transparent 8px);
                    background-size: 8px 1px, 8px 1px, 1px 8px, 1px 8px;
                    background-position: left top, left bottom, left top, right top;
                    background-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
                    padding: 30px 26px 30px 26px;
                    cursor: pointer;
                }
                .selected-container {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 30px 26px 30px 26px;
                }
                .selected-image {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .upload-button-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }
                .upload-button-container > span {
                    font-size: 12px;
                }
                .upload-button {
                    width: 40px;
                    height: 40px;
                    margin-bottom: 8px;
                }
                .change-image-button {
                    position: absolute;
                    bottom: 5%;
                    right: 5%;
                }
                .change-image-button > label > div {
                    border: none;
                    background: #fff;
                    padding: 4px 8px 4px 8px;
                    border-radius: 4px;
                    cursor: pointer;
                }
                select,
                input[type=text] {
                    padding-left: 14px;
                    padding-right: 14px;
                    width: 100%;
                    height: 38px;
                }
                .error-message {
                    color: red;
                    font-size: 4px;
                }
                p {
                    font-size: 8px;
                    margin: 0;
                }
                .spot-options {
                    display: flex;
                    flex-direction: column;
                    margin-bottom: 20px;
                }
                .option {
                    font-size: 16px;
                }
                .option,
                .spot-options > .label {
                    margin-bottom: 20px;
                }
                .option:nth-last-child(1) {
                    margin-bottom: 0px;
                }
                input[type=checkbox] + label {
                    padding-left: 12px;
                }
                input[type=checkbox]:checked + label {
                    font-weight: bold;
                }
                :global(.cancel-button) {
                    margin-right: 20px;
                }
                .warning {
                    background: #f5f5f5;
                    margin-bottom: 38px;
                }
                .warning > p {
                    font-size: 14px;
                    padding: 12px;
                }
                .action {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                .limit {
                    width: 100%;
                    text-align: right;
                    font-size: 4px;
                }
                @media screen and (max-width: 540px) {
                    .title {
                        padding-top: 24px;
                        padding-bottom: 20px;
                    }
                    .message {
                        font-size: 12px;
                    }
                    .label {
                        height: 18px;
                        font-size: 12px;
                    }
                    .wrapper {
                        margin-bottom: 12px;
                    }
                    .error-message {
                        font-size: 10px;
                    }
                    .option {
                        font-size: 14px;
                    }
                    .option,
                    .spot-options > .label {
                        margin-bottom: 12px;
                    }
                    .warning > p {
                        font-size: 10px;
                    }
                    .action {
                        justify-content: center;
                        align-items: center;
                    }
                }
            `}</style>
        </UserPage>
    );
};
